import { useEffect, useState } from 'react';
import "@/App.css";
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import { AppDashboard } from './components/AppDashboard';
import { LoginPage } from './components/auth/LoginPage';
import { SignupPage } from './components/auth/SignupPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { Toaster } from './components/ui/sonner';
import { initScrollTracking } from './utils/analytics';
import { Loader2 } from 'lucide-react';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PrivacyPage from './components/pages/PrivacyPage';
import TermsPage from './components/pages/TermsPage';
import CookiePage from './components/pages/CookiePage';
import BillingSuccess from './components/pages/BillingSuccess';
import BillingCancel from './components/pages/BillingCancel';
import BillingCompleteSetup from './components/pages/BillingCompleteSetup';
import MarketingPage from './components/pages/MarketingPage';
import CapabilityStatementPage from './components/pages/CapabilityStatementPage';
import { SEOProvider, SEOHead } from './components/SEOHead';

// SEO configurations for different pages
const SEO_CONFIG = {
  '/': {
    title: 'D3 Command | AI-Powered Operational Governance Platform',
    description: 'Transform conversations into execution, analytics, and compliance. D3 Command is your AI-powered control center for operational excellence.',
    keywords: 'AI meeting intelligence, workflow automation, compliance, governance, enterprise platform, operational excellence',
  },
  '/about': {
    title: 'About Us',
    description: 'Learn about D3 Command - the AI-powered platform transforming how enterprises manage meetings, workflows, and compliance.',
    keywords: 'about D3 Command, AI company, enterprise software, meeting intelligence company',
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with the D3 Command team. We\'re here to help you transform your operational governance.',
    keywords: 'contact D3 Command, support, sales, enterprise inquiry',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'D3 Command Privacy Policy - Learn how we protect your data and maintain your privacy.',
    keywords: 'privacy policy, data protection, GDPR, security',
  },
  '/terms-of-service': {
    title: 'Terms of Service',
    description: 'D3 Command Terms of Service - Our terms and conditions for using the platform.',
    keywords: 'terms of service, legal, terms and conditions',
  },
  '/cookie-policy': {
    title: 'Cookie Policy',
    description: 'D3 Command Cookie Policy - How we use cookies to improve your experience.',
    keywords: 'cookie policy, cookies, tracking',
  },
  '/login': {
    title: 'Sign In',
    description: 'Sign in to your D3 Command account to access your AI-powered operational governance dashboard.',
    keywords: 'login, sign in, D3 Command account',
  },
  '/signup': {
    title: 'Create Account',
    description: 'Create your D3 Command account and start transforming your operational governance with AI.',
    keywords: 'sign up, create account, register, free trial',
  },
};

const AppRoutes = () => {
  const { isAuthenticated, billingRequired, isDemo, loading } = useAuth();
  const [route, setRoute] = useState(window.location.pathname);
  const [authView, setAuthView] = useState('login');

  useEffect(() => {
    const handleRouteChange = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    if (route !== '/dashboard' && route !== '/app' && route !== '/login' && route !== '/signup') {
      const cleanup = initScrollTracking();
      return cleanup;
    }
  }, [route]);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setRoute(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Loading D3 Command...</p>
        </div>
      </div>
    );
  }

  // Root route — hostname-aware routing
  // app.d3command.com → redirect to login/dashboard (product app)
  // www.d3command.com / all others → show marketing landing page
  // Updated: Apr 2026 — dual-domain deployment support
  if (route === '/' || route === '/company') {
    const hostname = window.location.hostname;
    const isAppDomain = hostname === 'app.d3command.com';

    if (isAppDomain && route === '/') {
      if (isAuthenticated) {
        navigate('/dashboard');
        return null;
      }
      navigate('/login');
      return null;
    }

    return (
      <ThemeProvider>
        <RouteSEO route={route} />
        <MarketingPage />
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    );
  }

  // Footer pages
  const footerPages = {
    '/about': AboutPage,
    '/contact': ContactPage,
    '/privacy-policy': PrivacyPage,
    '/terms-of-service': TermsPage,
    '/cookie-policy': CookiePage,
  };

  const FooterPageComponent = footerPages[route];
  if (FooterPageComponent) {
    return (
      <ThemeProvider>
        <RouteSEO route={route} />
        <div className="min-h-screen bg-[#0A0A0B] text-white">
          <Navbar onDashboardClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')} />
          <FooterPageComponent />
          <Toaster position="top-right" richColors />
        </div>
      </ThemeProvider>
    );
  }

  // Capability Statement - Confidential government contracting page (no navigation)
  if (route === '/capability-statement') {
    return (
      <ThemeProvider>
        <CapabilityStatementPage />
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    );
  }

  // Billing routes — accessible regardless of billing status
  if (route === '/billing/success') {
    return (
      <>
        <BillingSuccess navigate={navigate} />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  if (route === '/billing/cancel') {
    return (
      <>
        <BillingCancel navigate={navigate} isAuthenticated={isAuthenticated} />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  if (route === '/billing/complete-setup') {
    if (!isAuthenticated) {
      navigate('/login');
      return null;
    }
    return (
      <>
        <BillingCompleteSetup navigate={navigate} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // ── BILLING GATE ──
  // If user is authenticated but has billingRequired=true,
  // redirect them to complete-setup (not dashboard)
  // Demo users bypass billing gate
  if (isAuthenticated && billingRequired && !isDemo) {
    if (route === '/dashboard' || route === '/app') {
      navigate('/billing/complete-setup');
      return null;
    }
  }

  if (route === '/dashboard' || route === '/app') {
    if (!isAuthenticated) {
      navigate('/login');
      return null;
    }
    return <AppDashboard />;
  }

  if (route === '/login' || route === '/signup') {
    if (isAuthenticated) {
      // If billing is required (and not demo), redirect to setup instead of dashboard
      if (billingRequired && !isDemo) {
        navigate('/billing/complete-setup');
      } else {
        navigate('/dashboard');
      }
      return null;
    }
    if (authView === 'forgot') {
      return (
        <>
          <ForgotPasswordPage onBack={() => setAuthView('login')} />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    if (authView === 'signup' || route === '/signup') {
      return (
        <>
          <RouteSEO route="/signup" />
          <SignupPage onSwitch={() => { setAuthView('login'); navigate('/login'); }} />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    return (
      <>
        <RouteSEO route="/login" />
        <LoginPage
          onSwitch={() => { setAuthView('signup'); navigate('/signup'); }}
          onForgot={() => setAuthView('forgot')}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Fallback — redirect based on hostname
  const hostname = window.location.hostname;
  if (hostname === 'app.d3command.com') {
    navigate(isAuthenticated ? '/dashboard' : '/login');
  } else {
    navigate('/');
  }
  return null;
};

// Helper component to add SEO based on current route
const RouteSEO = ({ route }) => {
  const seoConfig = SEO_CONFIG[route] || SEO_CONFIG['/'];
  const baseUrl = 'https://www.d3command.com';
  const noindex = ['/dashboard', '/billing', '/app'].some(p => route.startsWith(p));
  
  return (
    <SEOHead
      title={seoConfig.title}
      description={seoConfig.description}
      keywords={seoConfig.keywords}
      url={`${baseUrl}${route === '/' ? '' : route}`}
      noindex={noindex}
    />
  );
};

function App() {
  return (
    <SEOProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </SEOProvider>
  );
}

export default App;
